"use client";

import * as React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcnComponents/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import { Badge } from "@/shadcnComponents/ui/badge";
import { ScrollArea } from "@/shadcnComponents/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Bell, Circle } from "lucide-react";
import { useNotification } from "@/Providers/Notification";
import { useNavigate } from "react-router-dom";
import { formatRelativeTime } from "@/Helpers/times-ago";
import { Button } from "@/shadcnComponents/ui/button";

// Badge color mapping based on notification type with distinct colors
// const getBadgeVariant = (type: string) => {
//   const typeMap: Record<string, string> = {
//     Profile: "bg-[#FF0000] text-white hover:bg-[#FF0000]/90",
//     Campaign: "bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90", // Blue
//     Subscription: "bg-[#10B981] text-white hover:bg-[#10B981]/90", // Green
//   }

//   return typeMap[type] || "bg-gray-500 text-white hover:bg-gray-600"
// }

export function BrandNotificationPanel() {
  const notificationState = useNotification();
  const [isOpen, setIsOpen] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState<"all" | "unread" | "read">(
    "all"
  );

  const unreadCount = notificationState?.unreadCount;
  const navigate = useNavigate();

  const filteredNotifications = React.useMemo(() => {
    if (activeTab === "unread") return notificationState?.unreadNotifications;
    if (activeTab === "read") return notificationState?.readNotifications;
    return notificationState?.notifications;
  }, [activeTab, notificationState]);

  const markAsRead = (id: string) => {
    notificationState?.markAsRead(id);
  };

  //   const markAllAsRead = () => {
  //     notificationState?.markAllAsRead();
  //   };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          variant="default"
          className="relative text-black bg-transparent hover:bg-transparent hover:text-red-300"
          onClick={() => setIsOpen(true)}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white border-none">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md p-0">
        <SheetHeader className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <SheetTitle>Notifications</SheetTitle>
          </div>
        </SheetHeader>

        <div className="px-4">
          <Tabs
            defaultValue="all"
            value={activeTab}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onValueChange={(value) => setActiveTab(value as any)}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
              <TabsTrigger value="read">Read</TabsTrigger>
            </TabsList>

            <TabsContent
              value={activeTab}
              className="mt-4 h-[calc(100vh-140px)]"
            >
              <ScrollArea className="h-full pr-4">
                <div className="space-y-4 pb-6">
                  {filteredNotifications?.length === 0 ? (
                    <div className="flex h-40 items-center justify-center text-muted-foreground">
                      No notifications to display
                    </div>
                  ) : (
                    filteredNotifications?.map((notification) => (
                      <Card
                        key={notification?._id}
                        className={cn(
                          "relative bg-white cursor-pointer transition-all border-b rounded-lg overflow-hidden",
                          // Premium shadow for all cards
                          "shadow-sm hover:shadow-md",
                          // Only highlight unread notifications in the "all" tab with a subtle left border
                          activeTab === "all" &&
                            !notification?.read &&
                            "border-l-4 border-l-[#FF0000]/70"
                        )}
                        onClick={() => {
                          if (!notification?.read) {
                            markAsRead(notification?._id);
                          }
                          if (notification?.redirectionLink) {
                            navigate(notification?.redirectionLink);
                          }
                          if (notification?.externalLink) {
                            window.open(notification?.externalLink, "_blank");
                          }
                        }}
                      >
                        <CardHeader className="pb-2 pt-4 px-5">
                          <div className="flex flex-col">
                            <div className="mb-2 flex flex-wrap gap-2">
                              {notification?.type === "campaign" ? (
                                <>
                                  <Badge
                                    variant="outline"
                                    className="text-xs uppercase tracking-wider text-gray-600 bg-gray-100 hover:bg-gray-200 border-gray-200"
                                  >
                                    {notification?.type}
                                  </Badge>
                                  {notification?.campaign_ID && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs uppercase tracking-wider text-gray-600 bg-gray-100 hover:bg-gray-200 border-gray-200"
                                    >
                                      ID: {notification?.campaign_ID}
                                    </Badge>
                                  )}
                                </>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-xs uppercase tracking-wider text-gray-600 bg-gray-100 hover:bg-gray-200 border-gray-200"
                                >
                                  {notification?.type}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-start gap-2">
                              {activeTab === "all" && !notification?.read && (
                                <Circle className="h-2 w-2 mt-1.5 fill-[#FF0000] text-[#FF0000] flex-shrink-0" />
                              )}
                              <CardTitle
                                className={cn(
                                  "text-base text-gray-800",
                                  // Only bold titles of unread notifications in the "all" tab
                                  activeTab === "all" &&
                                    !notification?.read &&
                                    "font-bold"
                                )}
                              >
                                {notification?.heading}
                              </CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 px-5 pl-8 pb-4">
                          <p
                            className={cn(
                              "text-sm mb-3 flex flex-col gap-3 items-start leading-relaxed",
                              // Only use different text color in the "all" tab
                              activeTab === "all" && !notification?.read
                                ? "text-gray-700"
                                : "text-gray-500"
                            )}
                          >
                            {notification?.subheading}
                            {/* cta button if cta is present */}
                            {notification?.cta && (
                              <Button
                                onClick={() => {
                                  if (notification?.redirectionLink) {
                                    navigate(notification?.redirectionLink);
                                  }
                                  if (notification?.externalLink) {
                                    window.open(
                                      notification?.externalLink,
                                      "_blank"
                                    );
                                  }
                                }}
                                size="sm"
                                className="text-xs"
                              >
                                {notification?.cta}
                              </Button>
                            )}
                          </p>
                          <p className="text-xs text-gray-400 font-medium">
                            {formatRelativeTime(notification?.createdAt)}
                          </p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
